import clsx from 'clsx'
import { t } from 'i18next'
import { observer } from 'mobx-react'
import { FilterMatchMode } from 'primereact/api'
import { Paginator } from 'primereact/paginator'
import { useEffect, useMemo } from 'react'
import { useIsMobile } from 'utils/hooks/useIsMobile'
import { useObjectSearchParams } from 'utils/hooks/useObjectSearchParams'
import ListView from './ListView'
import TableView from './TableView'
import { IBaseManagementComponentProps } from './types'

const BaseManagementComponent = ({
  filterComponent = <></>,
  columns = [],
  rowClassName,
  loading,
  actionWidth,
  dataSource = [],
  actionColumns,
  handleFilterDataChange,
  pagination,
  showActionColumn = true,
  mobileListRender,
}: IBaseManagementComponentProps) => {
  const { searchObject, setRestSearchObject } = useObjectSearchParams()
  const isMobile = useIsMobile()

  const sortObject = useMemo<any>(() => {
    const sortArray = searchObject.sortBy ? searchObject.sortBy.split(':') : []
    if (sortArray.length)
      return {
        sortField: sortArray[0]?.replaceAll('"', ''),
        sortOrder: Number(sortArray[1]),
      }
    return {}
  }, [searchObject])

  useEffect(() => {
    if (searchObject) handleFilterDataChange && handleFilterDataChange(searchObject)
  }, [handleFilterDataChange, searchObject])

  const filters = useMemo(() => {
    const obj: any = {}
    columns
      .filter((col) => col.filter)
      .forEach((col) => {
        obj[col.filterField ?? col.key] = { value: null, matchMode: FilterMatchMode.CUSTOM }
      })
    Object.keys(searchObject).forEach((key) => {
      if (key !== 'page' && key !== 'perPage' && key !== 'sortBy') {
        obj[key] = { value: searchObject[key], matchMode: FilterMatchMode.CUSTOM }
      }
    })
    return obj
  }, [columns, searchObject])

  const shouldUseMobileList = isMobile && !!mobileListRender

  return (
    <div
      className={clsx('card bg-white border-round-xl p-3 pb-6 overflow-auto')}
      style={{ minHeight: loading ? '480px' : '' }}
    >
      {filterComponent}
      {shouldUseMobileList ? (
        <ListView
          dataSource={dataSource}
          loading={loading}
          mobileListRender={mobileListRender}
        />
      ) : (
        <TableView
          dataSource={dataSource}
          columns={columns}
          loading={loading}
          filters={filters}
          sortObject={sortObject}
          rowClassName={rowClassName}
          showActionColumn={showActionColumn}
          actionWidth={actionWidth}
          actionColumns={actionColumns}
          setRestSearchObject={setRestSearchObject}
        />
      )}
      {pagination && pagination.total && pagination.page ? (
        <Paginator
          first={(pagination.page - 1) * pagination.perPage}
          rows={pagination.perPage}
          totalRecords={pagination.total}
          template={{
            layout: isMobile
              ? 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink'
              : 'CurrentPageReport FirstPageLink PrevPageLink  PageLinks NextPageLink LastPageLink RowsPerPageDropdown',
            CurrentPageReport: (options) => (
              <span>
                {isNaN(options.first) ? 0 : options.first} - {isNaN(options.last) ? 0 : options.last ?? 0}{' '}
                of {options.totalRecords ?? 0}
              </span>
            ),
          }}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={(e) =>
            setRestSearchObject({
              page: e.page + 1,
              perPage: e.rows,
            })
          }
          className="justify-content-center base-management-paginator"
        />
      ) : null}
    </div>
  )
}

export default observer(BaseManagementComponent)
